import React, { useState, useEffect, useRef } from 'react';

import DocTypeSelector from './selector/DocTypeSelector';
import { useAppDispatch, useAppSelector } from '../../hooks/customHooks';
import {
  SContent,
  SContentWrapper,
  SDivider,
  SDropdownContainer,
  SDropdownIcon,
  SInstanceInfo,
  SPanelContainer,
  SSearchIcon,
  SSelectorContainer,
  SSelectorDiv,
  SSelectorIcon,
  STopBar,
  SWrapper,
} from './styles/TopBar.styled';
import { fetchCustomers } from '../../features/customersSlice';
import { SLoader } from '@/presentation/loader/Loader.styled';
import CustomerSelector from './selector/CustomerSelector';
import { faBuilding, faFileLines, faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { useParams } from 'react-router-dom';
import Text from '@/presentation/text';
import ModalGlobalSearch from '../shared/modal/ModalGlobalSearch';
import { setActiveMessage } from '@/features/messagesSlice';

interface IProps {
  isTopBarUsable: boolean;
  isSysPrinDocTypeHidden: boolean;
}

const TopBar: React.FC<IProps> = ({ isTopBarUsable, isSysPrinDocTypeHidden }) => {
  const username = useAppSelector((state) => state.auth.username);
  const availableInstances = useAppSelector((state) => state.auth.availableInstances);
  const customerState = useAppSelector((state) => state.customers);
  const docTypeState = useAppSelector((state) => state.docTypes);
  const dispatch = useAppDispatch();

  const [isCustomerSelectorOpen, setIsCustomerSelectorOpen] = useState(false);
  const [isDocTypeSelectorOpen, setIsDocTypeSelectorOpen] = useState(false);
  const customerOpenerRef = useRef<HTMLDivElement>(null);
  const customerRef = useRef<HTMLDivElement>(null);
  const docTypeOpenerRef = useRef<HTMLDivElement>(null);
  const docTypeRef = useRef<HTMLDivElement>(null);
  const { sysPrin, docTypeId } = useParams();
  const [openGlobalSearchModal, setOpenGlobalSearchModal] = useState(false);

  const handleCloseGlobalSearchModal = () => {
    setOpenGlobalSearchModal(false);
  };

  useEffect(() => {
    if (!customerState.activeCustomer && !docTypeState.activeDocType)
      dispatch(fetchCustomers({ sysPrinInUrl: sysPrin, docTypeIdInUrl: docTypeId }));

    const close = (e: MouseEvent) => {
      if (customerOpenerRef.current?.contains(e.target as Node)) {
        setIsCustomerSelectorOpen(true);
      }
      if (customerRef && customerRef.current && !customerRef.current.contains(e.target as Node)) {
        setIsCustomerSelectorOpen(false);
      }
      if (docTypeOpenerRef.current?.contains(e.target as Node)) {
        setIsDocTypeSelectorOpen(true);
      }
      if (docTypeRef && docTypeRef.current && !docTypeRef.current.contains(e.target as Node)) {
        setIsDocTypeSelectorOpen(false);
      }
    };
    document.addEventListener('click', close, { capture: true }); // If you click outside of the selector, close it

    return () => {
      document.removeEventListener('click', close, { capture: true });
    };
  }, []);

  useEffect(() => {
    setIsDocTypeSelectorOpen(false);
    dispatch(setActiveMessage(null));
  }, [docTypeState.activeDocType]);

  useEffect(() => {
    setIsCustomerSelectorOpen(false);
  }, [customerState.activeCustomer]);

  const isCustomerSelectorActive = isCustomerSelectorOpen && !customerState.customers.isRequestActive;
  const isDocTypeSelectorActive =
    isDocTypeSelectorOpen && !customerState.customers.isRequestActive && !docTypeState.isRequestActive;

  const displayInstance = () => {
    let strName = '';
    const activeInstance = availableInstances?.find((inst) => inst.isActive === true);
    const displyedInstance = availableInstances === undefined ? '' : availableInstances[0]?.displayName;
    if (activeInstance !== undefined) {
      strName = activeInstance.displayName;
    }
    if (activeInstance === undefined) {
      strName = displyedInstance;
    }
    return strName;
  };

  return (
    <STopBar>
      <SWrapper>
        <SPanelContainer isSysPrinDocTypeHidden={isSysPrinDocTypeHidden} isTopBarUsable={isTopBarUsable}>
          <SSelectorContainer>
            <SSelectorDiv
              id="ddlCustomer"
              ref={customerOpenerRef}
              active={isCustomerSelectorActive}
              valueType="customer"
              className="hoverable"
            >
              <SSelectorIcon>
                <FontAwesomeIcon icon={faBuilding} />
              </SSelectorIcon>

              <SContentWrapper>
                {customerState.customers.isRequestActive ? (
                  <SLoader />
                ) : (
                  <>
                    <SContent id="lblCustomerName" valueType="title">
                      {customerState.activeCustomer ? customerState.activeCustomer.name : ''}
                    </SContent>
                    <SContent id="lblCustomerSysPrin" valueType="value">
                      {customerState.activeCustomer ? customerState.activeCustomer.sysPrin.sysPrin : ''}
                    </SContent>
                  </>
                )}
              </SContentWrapper>
              <SDropdownContainer isSysPrinDocTypeHidden={isSysPrinDocTypeHidden} isTopBarUsable={isTopBarUsable}>
                <SDropdownIcon id="btnCustomerCaret">
                  <FontAwesomeIcon icon={faCaretDown} />
                </SDropdownIcon>
              </SDropdownContainer>
            </SSelectorDiv>
            <SDivider isSysPrinDocTypeHidden={isSysPrinDocTypeHidden} isTopBarUsable={isTopBarUsable} />
            <SSelectorDiv
              id="ddlDocType"
              ref={docTypeOpenerRef}
              active={isDocTypeSelectorActive}
              valueType="docType"
              className="hoverable"
            >
              <SSelectorIcon>
                <FontAwesomeIcon icon={faFileLines} />
              </SSelectorIcon>

              <SContentWrapper>
                {customerState.customers.isRequestActive || docTypeState.isRequestActive ? (
                  <SLoader />
                ) : (
                  <>
                    <SContent id="lblDocType" valueType="title">
                      DOC-TYPE
                    </SContent>
                    <SContent id="lblActiveDocType" valueType="value">
                      {docTypeState.activeDocType ? docTypeState.activeDocType.name : ''}
                    </SContent>
                  </>
                )}
              </SContentWrapper>
              <SDropdownContainer isSysPrinDocTypeHidden={isSysPrinDocTypeHidden} isTopBarUsable={isTopBarUsable}>
                <SDropdownIcon id="btnDocTypeCaret">
                  <FontAwesomeIcon icon={faCaretDown} />
                </SDropdownIcon>
              </SDropdownContainer>
            </SSelectorDiv>
          </SSelectorContainer>
          {isCustomerSelectorActive ? (
            <div ref={customerRef}>
              <CustomerSelector />
            </div>
          ) : null}
          {isDocTypeSelectorActive ? (
            <div ref={docTypeRef}>
              <DocTypeSelector />
            </div>
          ) : null}
        </SPanelContainer>
        <SDivider isSysPrinDocTypeHidden={isSysPrinDocTypeHidden} isTopBarUsable={isTopBarUsable} />
        <SSearchIcon
          isSysPrinDocTypeHidden={isSysPrinDocTypeHidden}
          isTopBarUsable={isTopBarUsable}
          id="btnSearch"
          className=" hoverable"
          onClick={() => {
            setOpenGlobalSearchModal(true);
          }}
        >
          <FontAwesomeIcon icon={faSearch} />
        </SSearchIcon>
      </SWrapper>
      {availableInstances && username && (
        <SInstanceInfo
          id="lblLoggedInAsHeader"
          backgroundColor={(window as any).CSG_CONFIG.ENVIRONMENT_INSTANCE_BACKGROUND_COLOR}
        >
          <Text size="xxs" color="#FFF" weight="bold">
            Logged in as {username} in {displayInstance()}
          </Text>
        </SInstanceInfo>
      )}
      {setOpenGlobalSearchModal && (
        <ModalGlobalSearch
          show={openGlobalSearchModal}
          setShow={setOpenGlobalSearchModal}
          title=" Search Options"
          onClose={handleCloseGlobalSearchModal}
        />
      )}
    </STopBar>
  );
};

export default TopBar;
